/**
 * Departments a project (task list) can belong to.
 *
 * The Tasks section is the cross-company hub: it lists every project
 * the user can see and every task assigned to them, regardless of
 * department. A department section (Engineering today, Marketing
 * Projects later, …) can then surface just its own projects by
 * filtering on `projects.department` — e.g.
 * `useProjects({ department: 'engineering' })` for a sidebar list, or a
 * `/tasks?department=engineering` link for the filtered hub.
 *
 * The list is code-defined (like sidebar sections) rather than a
 * database table: adding a department is a one-line change here and
 * the column is free text (migration 141), so nothing has to migrate.
 * Slugs are what gets stored; labels are display-only and safe to
 * reword.
 */

export type DepartmentSlug =
  | 'engineering'
  | 'marketing'
  | 'sales'
  | 'operations'
  | 'quality'
  | 'finance'
  | 'hr';

export interface DepartmentMeta {
  slug: DepartmentSlug;
  label: string;
  /** Short chip tint — brand accents as wayfinding only (BRAND.md). */
  chipClassName: string;
}

export const DEPARTMENTS: readonly DepartmentMeta[] = [
  {
    slug: 'engineering',
    label: 'Engineering',
    chipClassName: 'bg-atmosphere/40 text-atmosphere-dark',
  },
  {
    slug: 'marketing',
    label: 'Marketing',
    chipClassName: 'bg-bright-moss/35 text-bright-moss-dark',
  },
  {
    slug: 'sales',
    label: 'Sales',
    chipClassName: 'bg-old-growth/15 text-old-growth',
  },
  {
    slug: 'operations',
    label: 'Operations',
    chipClassName: 'bg-humus/10 text-humus',
  },
  {
    slug: 'quality',
    label: 'Quality',
    chipClassName: 'bg-info-soft/40 text-info',
  },
  {
    slug: 'finance',
    label: 'Finance',
    chipClassName: 'bg-linen text-humus',
  },
  {
    slug: 'hr',
    label: 'HR',
    chipClassName: 'bg-secondary text-secondary-foreground',
  },
] as const;

const BY_SLUG = new Map<string, DepartmentMeta>(
  DEPARTMENTS.map((d) => [d.slug, d]),
);

/** True when `value` is one of the known department slugs. */
export function isDepartmentSlug(
  value: string | null | undefined,
): value is DepartmentSlug {
  return !!value && BY_SLUG.has(value);
}

/**
 * Display metadata for a stored department value. Unknown values
 * (a slug that was later removed from the list) still render so a
 * project never loses its label; they just get the neutral chip.
 */
export function departmentMeta(
  value: string | null | undefined,
): DepartmentMeta | null {
  if (!value) return null;
  const known = BY_SLUG.get(value);
  if (known) return known;
  return {
    slug: value as DepartmentSlug,
    label: value.charAt(0).toUpperCase() + value.slice(1).replace(/[-_]/g, ' '),
    chipClassName: 'bg-secondary text-secondary-foreground',
  };
}

/** Label for a department slug, or `fallback` when the project has none. */
export function departmentLabel(
  value: string | null | undefined,
  fallback = 'No department',
): string {
  return departmentMeta(value)?.label ?? fallback;
}

/**
 * Normalise a `?department=` URL value: known slugs pass through,
 * anything else means "all departments".
 */
export function parseDepartmentParam(
  value: string | null | undefined,
): DepartmentSlug | null {
  return isDepartmentSlug(value) ? value : null;
}
